import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const AD_INTERVAL_SECONDS = 180; // default seconds of listening before an ad (per-station override via station.ad_interval)

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // station object
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(null);
  const [isAd, setIsAd] = useState(false);
  const [ad, setAd] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [sleepAt, setSleepAt] = useState(null);
  const sleepTimerRef = useRef(null);

  const setSleepTimer = (minutes) => {
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); sleepTimerRef.current = null; }
    if (!minutes) { setSleepAt(null); return; }
    setSleepAt(Date.now() + minutes * 60000);
    sleepTimerRef.current = setTimeout(() => {
      try { playIntent.current = false; audioRef.current.pause(); } catch (e) {}
      setSleepAt(null);
      sleepTimerRef.current = null;
    }, minutes * 60000);
  };

  const adQueue = useRef([]);
  const adIdx = useRef(0);
  const jIdx = useRef(0);
  const mixRef = useRef(30); // % of breaks that should be a jingle when both exist
  const seconds = useRef(0);
  const currentRef = useRef(null);
  const isAdRef = useRef(false);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const lastProgress = useRef(Date.now());
  const swapCount = useRef(0);
  const playIntent = useRef(false);      // does the user want audio playing?
  const programmaticLoad = useRef(false); // suppress auto-recovery during our own src reloads

  // Load a source and start playback, flagging it as a programmatic (expected) load.
  const loadAndPlay = (src) => {
    const audio = audioRef.current;
    programmaticLoad.current = true;
    audio.src = src;
    audio.load();
    lastProgress.current = Date.now();
    audio.play().catch(() => {});
    setTimeout(() => { programmaticLoad.current = false; }, 1500);
  };

  // Keep iOS/Android happy with lock-screen media controls & a live audio session.
  const updateMediaSession = (station) => {
    if (!("mediaSession" in navigator)) return;
    try {
      const origin = window.location.origin;
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: station?.name || "In His Image Radio",
        artist: "In His Image Radio",
        artwork: [
          { src: `${origin}/logo.png`, sizes: "192x192", type: "image/png" },
          { src: `${origin}/logo.png`, sizes: "512x512", type: "image/png" },
        ],
      });
      navigator.mediaSession.setActionHandler("play", () => togglePlay());
      navigator.mediaSession.setActionHandler("pause", () => togglePlay());
    } catch (e) {}
  };

  // Quietly switch to a healthy station when the current stream keeps failing.
  const autoSwap = async () => {
    if (swapCount.current >= 3) {
      setPlaying(false); setLoading(false); setReconnecting(false);
      setError("Streams are unavailable right now. Please try again shortly.");
      return;
    }
    swapCount.current += 1;
    try {
      const excl = currentRef.current?.id || "";
      const { data } = await api.get(`/stations/next-healthy?exclude=${excl}`);
      if (data && data.id) {
        const downName = currentRef.current?.name;
        toast.info(`${downName || "That station"} went quiet — switched to ${data.name}`);
        playStationInternal(data, false);
      } else {
        setPlaying(false); setLoading(false); setReconnecting(false);
        setError("All streams are unavailable right now. Please try again shortly.");
      }
    } catch (e) {
      setPlaying(false); setLoading(false); setReconnecting(false);
      setError("Stream unavailable. Try another station.");
    }
  };

  if (!audioRef.current) {
    audioRef.current = new Audio();
    audioRef.current.preload = "none";
  }

  // Fetch the ad rotation feed (scheduled spots with remaining plays + station jingles).
  useEffect(() => {
    const fetchRotation = () => {
      const sid = currentRef.current?.id;
      api.get(`/spots/rotation${sid ? `?station_id=${sid}` : ""}`).then((res) => { adQueue.current = res.data || []; }).catch(() => {});
      api.get("/jingles/mix").then((res) => { mixRef.current = res.data?.jingle_mix ?? 30; }).catch(() => {});
    };
    fetchRotation();
    const t = setInterval(fetchRotation, 60000);
    return () => clearInterval(t);
  }, [current?.id]);

  const clearReconnect = () => { if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; } };

  // Auto-reconnect a dropped live stream with backoff so playback recovers on its own.
  const scheduleReconnect = () => {
    if (isAdRef.current || !currentRef.current) return;
    if (reconnectTimer.current) return;
    if (reconnectAttempts.current >= 8) {
      setPlaying(false);
      setLoading(false);
      setReconnecting(false);
      autoSwap();
      return;
    }
    reconnectAttempts.current += 1;
    setLoading(true);
    setReconnecting(true);
    setError(null);
    const delay = Math.min(1000 * reconnectAttempts.current, 5000);
    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null;
      if (isAdRef.current || !currentRef.current) return;
      loadAndPlay(`${API}/stream/${currentRef.current.id}?_=${Date.now()}`);
    }, delay);
  };

  const resumeStation = () => {
    isAdRef.current = false;
    setIsAd(false);
    setAd(null);
    seconds.current = 0;
    reconnectAttempts.current = 0;
    clearReconnect();
    if (currentRef.current) {
      loadAndPlay(`${API}/stream/${currentRef.current.id}`);
    }
  };

  const playAd = () => {
    const queue = adQueue.current;
    if (!queue.length || isAdRef.current || !currentRef.current) return;
    const spots = queue.filter((x) => x.kind !== "jingle");
    const jingles = queue.filter((x) => x.kind === "jingle");
    let useJingle;
    if (spots.length && jingles.length) useJingle = Math.random() * 100 < mixRef.current;
    else useJingle = jingles.length > 0 && spots.length === 0;
    const pool = useJingle ? jingles : spots;
    if (!pool.length) return;
    const idxRef = useJingle ? jIdx : adIdx;
    const spot = pool[idxRef.current % pool.length];
    idxRef.current += 1;
    seconds.current = 0;
    isAdRef.current = true;
    setIsAd(true);
    setAd(spot);
    const isJingle = spot.kind === "jingle";
    loadAndPlay(`${API}/${isJingle ? "jingles" : "spots"}/${spot.id}/audio`);
    api.post(`/${isJingle ? "jingles" : "spots"}/${spot.id}/played`).catch(() => {});
  };

  useEffect(() => {
    const audio = audioRef.current;
    const onPlaying = () => {
      setPlaying(true); setLoading(false); setError(null); setReconnecting(false);
      reconnectAttempts.current = 0; swapCount.current = 0; clearReconnect(); lastProgress.current = Date.now();
      if ("mediaSession" in navigator) { try { navigator.mediaSession.playbackState = "playing"; } catch (e) {} }
    };
    const onPause = () => {
      setPlaying(false);
      if ("mediaSession" in navigator) { try { navigator.mediaSession.playbackState = "paused"; } catch (e) {} }
      // iOS often fires `pause` (not error/ended) when a live stream drops or the
      // audio session is interrupted. If the user still wants audio, recover.
      if (!isAdRef.current && playIntent.current && currentRef.current && !programmaticLoad.current) {
        scheduleReconnect();
      }
    };
    const onWaiting = () => setLoading(true);
    const onEnded = () => { if (isAdRef.current) resumeStation(); else scheduleReconnect(); };
    const onStalled = () => { if (!isAdRef.current) scheduleReconnect(); };
    const onError = () => {
      setLoading(false);
      if (isAdRef.current) { resumeStation(); return; }
      scheduleReconnect();
    };
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("stalled", onStalled);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("stalled", onStalled);
      audio.removeEventListener("error", onError);
    };
  }, []);

  // Stall watchdog: if playback makes no progress for a while, reconnect.
  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => { lastProgress.current = Date.now(); };
    audio.addEventListener("timeupdate", onTime);
    const t = setInterval(() => {
      if (playing && !isAdRef.current && currentRef.current && Date.now() - lastProgress.current > 12000) {
        scheduleReconnect();
      }
    }, 4000);
    return () => { audio.removeEventListener("timeupdate", onTime); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // When the app returns to the foreground (e.g. iPhone unlocked), timers were
  // throttled while backgrounded — re-check and resume playback if it stalled.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden || !playIntent.current || isAdRef.current || !currentRef.current) return;
      const audio = audioRef.current;
      if (audio.paused || Date.now() - lastProgress.current > 8000) {
        reconnectAttempts.current = 0;
        clearReconnect();
        setReconnecting(true);
        loadAndPlay(`${API}/stream/${currentRef.current.id}?_=${Date.now()}`);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotation timer: count listening seconds, trigger an ad at the interval.
  useEffect(() => {
    const t = setInterval(() => {
      if (playing && !isAdRef.current && currentRef.current) {
        seconds.current += 1;
        const threshold = Number(currentRef.current.ad_interval) || AD_INTERVAL_SECONDS;
        if (seconds.current >= threshold) playAd();
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  useEffect(() => {
    audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const playStation = (station, isManual = true) => {
    const audio = audioRef.current;
    if (isManual) swapCount.current = 0;
    if (isAdRef.current) { isAdRef.current = false; setIsAd(false); setAd(null); }
    if (current && current.id === station.id && !isAdRef.current) {
      if (playing) { playIntent.current = false; audio.pause(); }
      else { playIntent.current = true; audio.play().catch(() => {}); }
      return;
    }
    setError(null);
    setLoading(true);
    setReconnecting(false);
    seconds.current = 0;
    reconnectAttempts.current = 0;
    clearReconnect();
    playIntent.current = true;
    setCurrent(station);
    currentRef.current = station;
    updateMediaSession(station);
    loadAndPlay(`${API}/stream/${station.id}`);
  };
  const playStationInternal = (station) => playStation(station, false);

  // Fully disconnect the stream (used on sign-out).
  const stop = () => {
    const audio = audioRef.current;
    playIntent.current = false;
    clearReconnect();
    reconnectAttempts.current = 0;
    try { audio.pause(); } catch (e) {}
    try { audio.removeAttribute("src"); audio.load(); } catch (e) {}
    adQueue.current = []; adIdx.current = 0; seconds.current = 0;
    isAdRef.current = false; currentRef.current = null;
    setIsAd(false); setAd(null);
    setPlaying(false); setLoading(false); setReconnecting(false); setError(null);
    setCurrent(null);
    if ("mediaSession" in navigator) {
      try { navigator.mediaSession.playbackState = "none"; navigator.mediaSession.metadata = null; } catch (e) {}
    }
  };

  // Sign-out (or any component) can dispatch this to hard-stop streaming.
  useEffect(() => {
    const onStop = () => stop();
    window.addEventListener("iihr:stop-playback", onStop);
    return () => window.removeEventListener("iihr:stop-playback", onStop);
  }, []);

  const togglePlay = () => {
    if (!current) return;
    const audio = audioRef.current;
    if (playing) { playIntent.current = false; audio.pause(); }
    else {
      playIntent.current = true;
      reconnectAttempts.current = 0;
      clearReconnect();
      setError(null);
      setReconnecting(false);
      updateMediaSession(current);
      if (!audio.src || audio.error) { loadAndPlay(`${API}/stream/${current.id}?_=${Date.now()}`); }
      else { audio.play().catch(() => {}); }
    }
  };

  return (
    <PlayerContext.Provider
      value={{ current, playing, loading, volume, setVolume, muted, setMuted, error, isAd, ad, reconnecting, sleepAt, setSleepTimer, playStation, togglePlay, stop }}
    >
      {children}
    </PlayerContext.Provider>
  );
}
